import type { ReactElement, ReactNode } from 'react';
import Select, {
  components,
  type OptionProps,
  type StylesConfig,
} from '@atlaskit/select';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

/**
 * Accent colors for a selected chip. Any object with these three resolved CSS
 * values works — e.g. the site-tile palette's `SiteTileColor` is assignable.
 */
export type ChipColor = {
  background: string;
  border: string;
  foreground: string;
};

const wrapperStyles = xcss({ width: '100%', alignSelf: 'stretch' });
const stackStyles = xcss({ width: '100%' });
const selectStyles = xcss({ width: '100%', maxWidth: '350px' });

/**
 * Internal react-select option. `value` is a stable string key derived from the
 * caller's `toValue`; the original `item` and its stored value ride along so we
 * can hand them back untouched in `onChange`.
 */
type InternalOption<TItem, TValue> = {
  value: string;
  label: string;
  description?: string | null;
  icon?: ReactNode;
  chipColor?: ChipColor;
  item: TItem;
  storedValue: TValue;
};

/** Live selection counts passed to `renderLabel`. */
export type SelectionCounts = { count: number; total: number };

export interface MultiSelectProps<TItem, TValue = string> {
  /** All selectable items. */
  items: readonly TItem[];
  /** Currently selected values (as produced by `toValue`). Order is preserved. */
  value: readonly TValue[];
  /** How to store a given item — the value pushed into the selection array. */
  toValue: (item: TItem) => TValue;
  /** Called with the new selection: the stored values and their source items. */
  onChange: (values: TValue[], items: TItem[]) => void;
  /** Primary line for each option (and the chip / value label). */
  getLabel: (item: TItem) => string;
  /** Optional subtle description rendered below the label. */
  getDescription?: (item: TItem) => string | null | undefined;
  /** Optional leading icon/avatar node for each option. */
  getIcon?: (item: TItem) => ReactNode;
  /**
   * Optional accent color for the selected chip (multi-select). When provided,
   * each chip gets a colored background/border/foreground — mirroring the Rovo
   * and Trello workspace selectors. Omit for default (neutral) chips.
   */
  getChipColor?: (item: TItem) => ChipColor;
  /** Allow selecting more than one item. Defaults to false (single-select). */
  isMulti?: boolean;
  /**
   * Maximum number of selections (multi-select only). Once reached, unselected
   * options are disabled; already-selected options stay removable. Omit for no
   * cap.
   */
  maxSelected?: number;
  inputId: string;
  placeholder?: string;
  /** Renders the field label above the select; receives the live counts. */
  renderLabel?: (counts: SelectionCounts) => ReactNode;
  isSearchable?: boolean;
  isClearable?: boolean;
  /** Disables the field (non-interactive). Defaults to false. */
  isDisabled?: boolean;
  /** Optional subtle helper message rendered below the field. */
  message?: ReactNode;
}

/** Stable string key for a stored value, used for option identity / matching. */
const keyOf = (value: unknown): string => String(value);

/** Coerce react-select's single/multi onChange payload into a plain array. */
const toArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : value ? [value as T] : [];

/**
 * Option row: optional checkbox (multi-select) + icon + label with an optional
 * subtle description. Modeled on the Rovo and Trello workspace selectors.
 */
const OptionRow = ({
  data,
  showCheckbox,
  isSelected,
}: {
  data: Pick<
    InternalOption<unknown, unknown>,
    'label' | 'description' | 'icon'
  >;
  showCheckbox: boolean;
  isSelected: boolean;
}): ReactElement => (
  <Inline space="space.100" alignBlock="center">
    {showCheckbox && (
      <input
        type="checkbox"
        checked={isSelected}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
    )}
    {data.icon}
    <Stack>
      <Text weight="medium">{data.label}</Text>
      {data.description && (
        <Text size="small" color="color.text.subtlest">
          {data.description}
        </Text>
      )}
    </Stack>
  </Inline>
);

/**
 * react-select Option adapter. Reads `isMulti` from `selectProps`, so it lives
 * at module scope with a stable identity instead of being rebuilt per render.
 */
const SelectOption = (
  props: OptionProps<InternalOption<unknown, unknown>, boolean>,
): ReactElement => (
  <components.Option {...props}>
    <OptionRow
      data={props.data}
      showCheckbox={Boolean(props.selectProps.isMulti)}
      isSelected={props.isSelected}
    />
  </components.Option>
);

/**
 * Chip styling for the current selection (multi-select). Reads the per-option
 * `chipColor`, so options without one keep react-select's default chip. Module
 * scope keeps a stable identity without memoization.
 */
const chipStyles: StylesConfig<InternalOption<unknown, unknown>, boolean> = {
  multiValue: (base, state) => {
    const color = state.data.chipColor;
    return color
      ? {
          ...base,
          backgroundColor: color.background,
          borderColor: color.border,
          borderStyle: 'solid',
          borderWidth: token('border.width', '1px'),
        }
      : base;
  },
  multiValueLabel: (base, state) =>
    state.data.chipColor
      ? { ...base, color: state.data.chipColor.foreground }
      : base,
  multiValueRemove: (base, state) =>
    state.data.chipColor
      ? { ...base, color: state.data.chipColor.foreground }
      : base,
};

/**
 * Presentational single/multi select for a list of items.
 *
 * Selection is always represented as an array (0–1 entries in single-select),
 * so callers store it uniformly. The component is intentionally decoupled from
 * consent context — pass data in via props and persist it in `onChange`.
 */
function MultiSelect<TItem, TValue = string>({
  items,
  value,
  toValue,
  onChange,
  getLabel,
  getDescription,
  getIcon,
  getChipColor,
  isMulti = false,
  maxSelected,
  inputId,
  placeholder,
  renderLabel,
  isSearchable = true,
  isClearable = true,
  isDisabled = false,
  message,
}: MultiSelectProps<TItem, TValue>): ReactElement {
  const options: InternalOption<TItem, TValue>[] = items.map((item) => {
    const storedValue = toValue(item);
    return {
      value: keyOf(storedValue),
      label: getLabel(item),
      description: getDescription?.(item),
      icon: getIcon?.(item),
      chipColor: getChipColor?.(item),
      item,
      storedValue,
    };
  });

  const optionByKey = new Map(options.map((option) => [option.value, option]));

  // Preserve the caller's selection order; drop values no longer available.
  const selectedOptions = value
    .map((storedValue) => optionByKey.get(keyOf(storedValue)))
    .filter(
      (option): option is InternalOption<TItem, TValue> => option != null,
    );

  const handleChange = (newValue: unknown) => {
    const chosen = toArray<InternalOption<TItem, TValue>>(newValue);
    onChange(
      chosen.map((option) => option.storedValue),
      chosen.map((option) => option.item),
    );
  };

  return (
    <Box xcss={wrapperStyles}>
      <Stack space="space.100" xcss={stackStyles}>
        <Stack space="space.050">
          {renderLabel && (
            <Box as="label" htmlFor={inputId}>
              {renderLabel({
                count: selectedOptions.length,
                total: items.length,
              })}
            </Box>
          )}
          <Box xcss={selectStyles}>
            <Select<InternalOption<TItem, TValue>, boolean>
              inputId={inputId}
              options={options}
              isMulti={isMulti}
              isDisabled={isDisabled}
              isSearchable={isSearchable}
              isClearable={isClearable}
              closeMenuOnSelect={!isMulti}
              hideSelectedOptions={!isMulti}
              placeholder={placeholder}
              value={isMulti ? selectedOptions : (selectedOptions[0] ?? null)}
              isOptionDisabled={
                isMulti && maxSelected != null
                  ? (option, selectValue) =>
                      selectValue.length >= maxSelected &&
                      !selectValue.some(
                        (selected) => selected.value === option.value,
                      )
                  : undefined
              }
              components={{ Option: SelectOption as typeof components.Option }}
              styles={
                chipStyles as StylesConfig<
                  InternalOption<TItem, TValue>,
                  boolean
                >
              }
              onChange={handleChange}
            />
          </Box>
        </Stack>
        {message != null && (
          <Text size="small" color="color.text.subtlest">
            {message}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

export default MultiSelect;
